import { Icon } from "@iconify/react";
import { FormikProps, getIn } from "formik";
import { ReactNode } from "react";
import { NOMINEE_RELATIONSHIP_OPTIONS } from "../../constants/NomineeConstants";
import { AddEmployeeReq, EmployeeNominee } from "../../types/Employee";
import {
  Box,
  ButtonSize,
  Center,
  HStack,
  IconButtonType,
  IconCTAButton,
  VStack,
} from "../utils";
import { Input, Label, TextArea } from "../shared/InputField";
import { Select } from "../shared/Select";

const emptyNominee = (): EmployeeNominee => ({
  name: "",
  relationship: "",
  percentage: 0,
  dateOfBirth: "",
  contact: "",
  email: "",
  address: "",
});

const emptyGuardian = (): EmployeeNominee => ({
  ...emptyNominee(),
  percentage: 0,
});

export function isMinor(dateOfBirth?: string) {
  if (!dateOfBirth) return false;
  const birth = new Date(dateOfBirth);
  if (Number.isNaN(birth.getTime())) return false;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const month = today.getMonth() - birth.getMonth();
  if (month < 0 || (month === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age < 18;
}

function NomineeField({
  label,
  required,
  error,
  children,
  className = "",
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <VStack className={`gap-2 ${className}`}>
      <Label className="text-sm font-normal text-labelColor">
        {label}
        {required && <span className="ml-1 text-primary">*</span>}
      </Label>
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </VStack>
  );
}

function fieldError(
  formik: FormikProps<AddEmployeeReq>,
  path: string
): string | undefined {
  return getIn(formik.touched, path) || formik.submitCount > 0
    ? getIn(formik.errors, path)
    : undefined;
}

export function NomineesEditor({
  formik,
}: {
  formik: FormikProps<AddEmployeeReq>;
}) {
  const nominees = formik.values.nominees || [];
  const totalPercentage = nominees.reduce(
    (sum, nominee) => sum + Number(nominee.percentage || 0) * 100,
    0
  );
  const hasInvalidTotal =
    nominees.length > 0 && Math.abs(totalPercentage - 100) > 0.01;

  return (
    <VStack className="gap-4">
      <HStack className="w-full flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <VStack className="gap-1">
          <p className="text-sm font-medium text-gray-dark">
            Total allocation: {totalPercentage.toFixed(2).replace(/\.00$/, "")}%
          </p>
          <p
            className={`mt-1 text-xs ${
              hasInvalidTotal ? "text-red-500" : "text-gray-light"
            }`}
          >
            {hasInvalidTotal
              ? "Nominee allocation must total 100%."
              : "Allocation is required to total 100% when nominees are added."}
          </p>
        </VStack>
        <IconCTAButton
          iconName="material-symbols:add-rounded"
          onClick={() =>
            formik.setFieldValue("nominees", [...nominees, emptyNominee()])
          }
        >
          Add nominee
        </IconCTAButton>
      </HStack>

      {nominees.map((nominee, index) => {
        const prefix = `nominees.${index}`;
        const minor = isMinor(nominee.dateOfBirth);
        return (
          <Box
            key={nominee.id || index}
            className="overflow-hidden rounded-lg border border-borderColor bg-white"
          >
            <HStack className="w-full items-center justify-between gap-4 border-b border-borderColor bg-white px-4 py-3">
              <HStack className="min-w-0 items-center gap-2">
                <Center className="h-8 w-8 shrink-0 rounded-md bg-tertiaryBg text-primary">
                  <Icon
                    aria-hidden="true"
                    icon="material-symbols:person-outline-rounded"
                    width={18}
                  />
                </Center>
                <VStack className="min-w-0 gap-1">
                  <p className="text-sm font-medium text-gray-dark">
                    {nominee.name || `Nominee ${index + 1}`}
                  </p>
                  {minor && (
                    <p className="text-xs text-gray-light">Minor nominee</p>
                  )}
                </VStack>
              </HStack>
              <IconCTAButton
                aria-label={`Remove nominee ${index + 1}`}
                title={`Remove nominee ${index + 1}`}
                iconName="material-symbols:delete-outline-rounded"
                iconClassName="text-red-600"
                buttonType={IconButtonType.NONE}
                buttonSize={ButtonSize.MEDIUM}
                onClick={() =>
                  formik.setFieldValue(
                    "nominees",
                    nominees.filter((_, nomineeIndex) => nomineeIndex !== index)
                  )
                }
              />
            </HStack>

            <div className="grid gap-4 p-4 md:grid-cols-2">
              <NomineeField
                label="Full name"
                required
                error={fieldError(formik, `${prefix}.name`)}
              >
                <Input
                  aria-label="Nominee full name"
                  placeholder="Nominee full name"
                  {...formik.getFieldProps(`${prefix}.name`)}
                />
              </NomineeField>
              <NomineeField
                label="Relationship"
                required
                error={fieldError(formik, `${prefix}.relationship`)}
              >
                <Select
                  aria-label="Nominee relationship"
                  options={NOMINEE_RELATIONSHIP_OPTIONS}
                  placeholder="Select relationship"
                  {...formik.getFieldProps(`${prefix}.relationship`)}
                />
              </NomineeField>
              <NomineeField
                label="Allocation"
                required
                error={fieldError(formik, `${prefix}.percentage`)}
              >
                <div className="relative">
                  <Input
                    aria-label="Nominee allocation percentage"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    className="pr-8"
                    value={Number(nominee.percentage || 0) * 100}
                    onBlur={() =>
                      formik.setFieldTouched(`${prefix}.percentage`)
                    }
                    onChange={(event) =>
                      formik.setFieldValue(
                        `${prefix}.percentage`,
                        Number(event.target.value || 0) / 100
                      )
                    }
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-light">
                    %
                  </span>
                </div>
              </NomineeField>
              <NomineeField
                label="Date of birth"
                required
                error={fieldError(formik, `${prefix}.dateOfBirth`)}
              >
                <Input
                  aria-label="Nominee date of birth"
                  type="date"
                  max={new Date().toISOString().slice(0, 10)}
                  value={nominee.dateOfBirth || ""}
                  onBlur={() => formik.setFieldTouched(`${prefix}.dateOfBirth`)}
                  onChange={(event) => {
                    const dateOfBirth = event.target.value;
                    formik.setFieldValue(`${prefix}.dateOfBirth`, dateOfBirth);
                    if (isMinor(dateOfBirth) && !nominee.guardian) {
                      formik.setFieldValue(
                        `${prefix}.guardian`,
                        emptyGuardian()
                      );
                    } else if (!isMinor(dateOfBirth) && nominee.guardian) {
                      formik.setFieldValue(`${prefix}.guardian`, undefined);
                    }
                  }}
                />
              </NomineeField>
              <NomineeField
                label="Contact number"
                required
                error={fieldError(formik, `${prefix}.contact`)}
              >
                <Input
                  aria-label="Nominee contact number"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit contact number"
                  value={nominee.contact || ""}
                  onBlur={() => formik.setFieldTouched(`${prefix}.contact`)}
                  onChange={(event) =>
                    formik.setFieldValue(
                      `${prefix}.contact`,
                      event.target.value.replace(/\D/g, "").slice(0, 10)
                    )
                  }
                />
              </NomineeField>
              <NomineeField
                label="Email"
                error={fieldError(formik, `${prefix}.email`)}
              >
                <Input
                  aria-label="Nominee email"
                  type="email"
                  placeholder="nominee@example.com"
                  {...formik.getFieldProps(`${prefix}.email`)}
                />
              </NomineeField>
              <NomineeField
                label="Address"
                required
                className="md:col-span-2"
                error={fieldError(formik, `${prefix}.address`)}
              >
                <TextArea
                  aria-label="Nominee address"
                  className="min-h-[80px] resize-y"
                  placeholder="Nominee address"
                  {...formik.getFieldProps(`${prefix}.address`)}
                />
              </NomineeField>
            </div>

            {minor && nominee.guardian && (
              <Box className="border-t border-borderColor p-4">
                <HStack className="mb-4 items-start gap-2">
                  <Icon
                    aria-hidden="true"
                    icon="material-symbols:shield-person-outline-rounded"
                    className="text-primary"
                    width={20}
                  />
                  <VStack className="gap-1">
                    <p className="text-sm font-medium text-gray-dark">
                      Guardian details
                    </p>
                    <p className="text-xs text-gray-light">
                      An adult guardian is required for a minor nominee.
                    </p>
                  </VStack>
                </HStack>
                <div className="grid gap-4 md:grid-cols-2">
                  <NomineeField
                    label="Full name"
                    required
                    error={fieldError(formik, `${prefix}.guardian.name`)}
                  >
                    <Input
                      aria-label="Guardian full name"
                      placeholder="Guardian full name"
                      {...formik.getFieldProps(`${prefix}.guardian.name`)}
                    />
                  </NomineeField>
                  <NomineeField
                    label="Relationship"
                    required
                    error={fieldError(
                      formik,
                      `${prefix}.guardian.relationship`
                    )}
                  >
                    <Select
                      aria-label="Guardian relationship"
                      options={NOMINEE_RELATIONSHIP_OPTIONS}
                      placeholder="Select relationship"
                      {...formik.getFieldProps(
                        `${prefix}.guardian.relationship`
                      )}
                    />
                  </NomineeField>
                  <NomineeField
                    label="Date of birth"
                    required
                    error={fieldError(formik, `${prefix}.guardian.dateOfBirth`)}
                  >
                    <Input
                      aria-label="Guardian date of birth"
                      type="date"
                      max={new Date().toISOString().slice(0, 10)}
                      {...formik.getFieldProps(
                        `${prefix}.guardian.dateOfBirth`
                      )}
                    />
                  </NomineeField>
                  <NomineeField
                    label="Contact number"
                    error={fieldError(formik, `${prefix}.guardian.contact`)}
                  >
                    <Input
                      aria-label="Guardian contact number"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="10-digit contact number"
                      value={nominee.guardian.contact || ""}
                      onBlur={() =>
                        formik.setFieldTouched(`${prefix}.guardian.contact`)
                      }
                      onChange={(event) =>
                        formik.setFieldValue(
                          `${prefix}.guardian.contact`,
                          event.target.value.replace(/\D/g, "").slice(0, 10)
                        )
                      }
                    />
                  </NomineeField>
                  <NomineeField
                    label="Email"
                    error={fieldError(formik, `${prefix}.guardian.email`)}
                  >
                    <Input
                      aria-label="Guardian email"
                      type="email"
                      placeholder="guardian@example.com"
                      {...formik.getFieldProps(`${prefix}.guardian.email`)}
                    />
                  </NomineeField>
                  <NomineeField
                    label="Address"
                    required
                    className="md:col-span-2"
                    error={fieldError(formik, `${prefix}.guardian.address`)}
                  >
                    <TextArea
                      aria-label="Guardian address"
                      className="min-h-[80px] resize-y"
                      placeholder="Guardian address"
                      {...formik.getFieldProps(`${prefix}.guardian.address`)}
                    />
                  </NomineeField>
                </div>
              </Box>
            )}
          </Box>
        );
      })}

      {!nominees.length && (
        <Center className="rounded-lg border border-dashed border-inputBorder bg-white px-6 py-9 text-center">
          <VStack className="items-center gap-1">
            <Icon
              aria-hidden="true"
              icon="material-symbols:group-outline-rounded"
              className="text-gray-light"
              width={32}
            />
            <p className="text-sm font-medium text-gray-dark">
              No nominees added
            </p>
            <p className="text-xs text-gray-light">
              Add nominee details when they are available.
            </p>
          </VStack>
        </Center>
      )}
    </VStack>
  );
}
